
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import { Icon } from '@descomplica/vue-components';
import { getPaddedDisplayNumber } from '@/helpers/string';
import { isMobileDevice } from '@/helpers/mobile';
import { WEEK_DAYS_NAMES, ONE_HOUR_IN_TIMESTAMP } from '@/helpers/date';
import {
  AppointmentModel,
  UserModel,
  CourseModel,
  ClassroomModel,
  LiveModel,
} from '@descomplica/dex-models';
import ProgressBar from './progress/ProgressBar.vue';
import LiveIcon from './LiveIcon.vue';
import Analytics from '@/helpers/analytics/';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType } from '@/helpers/analytics/types';
import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { getCourseCategory } from '@/helpers/category';
import { EVENT_SOURCE_TYPE, getEventLiveType } from '@/helpers/analytics/eventProperties';
import { isSourceTypeValid } from '@/helpers/lives';

export default Vue.extend({
  components: { Icon, ProgressBar, LiveIcon },
  props: {
    appointment: {
      required: true,
      type: Object as Vue.PropType<AppointmentModel>,
    },
  },
  computed: {
    name(): TranslateResult {
      return this.appointment.event ? this.appointment.event.name : this.$t('exam');
    },
    hour(): string {
      const startHours = getPaddedDisplayNumber(this.appointment.start.getHours());
      const startMinutes = getPaddedDisplayNumber(this.appointment.start.getMinutes());

      let endHours: String;
      let endMinutes: String;

      if (this.appointment.end) {
        endHours = getPaddedDisplayNumber(this.appointment.end.getHours());
        endMinutes = getPaddedDisplayNumber(this.appointment.end.getMinutes());
      } else {
        endHours = getPaddedDisplayNumber(this.appointment.start.getHours() + 1);
        endMinutes = getPaddedDisplayNumber(this.appointment.start.getMinutes());
      }

      return `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`;
    },
    date(): string {
      return `${getPaddedDisplayNumber(this.appointment.start.getDate())}/${getPaddedDisplayNumber(
        this.appointment.start.getMonth() + 1,
      )}`;
    },
    dayName(): string {
      return WEEK_DAYS_NAMES[this.appointment.start.getDay()];
    },
    formattedDate(): string {
      return `${this.dayName}. ${this.date}`;
    },
    isLessonDone(): boolean {
      return this.dateDiff > this.duration;
    },
    dateDiff(): number {
      const currentDate = new Date();
      return currentDate.getTime() - this.appointment.start.getTime();
    },
    duration(): number {
      if (!this.appointment.end) {
        return ONE_HOUR_IN_TIMESTAMP;
      }

      return this.appointment.end.getTime() - this.appointment.start.getTime();
    },
    isHappening(): boolean {
      return this.dateDiff > 0 && !this.isLessonDone;
    },
    progress(): number {
      return Math.round((this.dateDiff / this.duration) * 100);
    },
    isDisable(): boolean {
      return !(this.isLessonDone || this.isHappening);
    },
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    course(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    classroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.course.name,
        discipline: this.classroom.name,
        content_type: ANALYTICS_LABELS.tutoring,
        category: getCourseCategory(this.course),
      };
    },
  },
  methods: {
    goToLive(): void {
      const { event } = this.appointment;

      if (!event || !isSourceTypeValid(event.sourceType) || this.isDisable) {
        return;
      }

      if (event.sourceType === EVENT_SOURCE_TYPE.zoom && isMobileDevice()) {
        window.open(event.url);
        return;
      }

      this.trackingContentOpened(event);

      this.$router.push({
        name: 'live',
        params: {
          classroomSlug: this.$route.params.classroomSlug,
          liveId: event.id.toString(),
        },
      });
    },
    trackingContentOpened(event: LiveModel): void {
      Analytics.trackEvent(EVENT_LABELS.contentOpened, {
        ...this.defaultTrackingProps,
        live: this.name,
        live_type: getEventLiveType(event),
      });
    },
  },
});
